import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DatePicker, Space } from 'antd';
import { Card, CardContent, Typography, Grid, List, ListItem, ListItemText, Divider, Box, Skeleton, Avatar, Tooltip, IconButton, FormControl, Container, Input, InputLabel, Select, MenuItem, useMediaQuery, useTheme, Collapse, Chip, Paper } from '@mui/material';
import { styled } from '@mui/system';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentIcon from '@mui/icons-material/Payment';
import StatusIcon from '@mui/icons-material/AssignmentTurnedIn';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HistoryIcon from '@mui/icons-material/History';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import CakeIcon from '@mui/icons-material/Cake';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InboxIcon from '@mui/icons-material/Inbox';

import { message } from 'antd';
import moment from 'moment';

const { RangePicker } = DatePicker;
const StyledPage = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#f0f2f5',
  minHeight: '100vh',
  overflow: 'auto',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
}));
const StyledTitle = styled(Typography)(({ theme }) => ({
  color: '#1976d2',
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  textAlign: 'center',
  fontFamily: 'Roboto, sans-serif',
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  fontSize: '1.5rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '2rem',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '2.125rem',
    marginBottom: theme.spacing(4),
  },
}));
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  borderRadius: '10px',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.2)',
  },
}));
const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
}));
const StyledList = styled(List)(({ theme }) => ({
  '& .MuiListItem-root': {
    borderBottom: '1px solid #e0e0e0',
    padding: theme.spacing(1),
    flexDirection: 'column',
    alignItems: 'stretch',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      padding: theme.spacing(2),
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
}));
const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  '& .MuiTypography-body1': {
    fontWeight: 'bold',
    color: '#333',
    fontSize: '1.1rem',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.2rem',
      marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1.4rem',
    },
  },
  '& .MuiTypography-body2': {
    color: '#666',
    fontSize: '0.85rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '0.9rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '0.95rem',
    },
  },
}));
const MobileEventCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  '& .MuiCardContent-root': {
    padding: theme.spacing(2),
  },
}));

const FiltersContainer = styled(Container)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  [theme.breakpoints.up('md')]: {
    marginTop: theme.spacing(4),
  },
}));
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return format(date, "dd/MM/yyyy hh:mm a", { locale: es });
};

function ViewEvents() {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState("");
  const [dates, setDates] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // Nuevo estado para el filtro de estado de pago
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [emptyResponse, setEmptyResponse] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [error, setError] = useState(false);
  const [initializedFromURL, setInitializedFromURL] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const token = localStorage.getItem('token');

  const fetchUpcomingEvents = async () => {
    setIsLoading(true);
    let accessToken = token || localStorage.getItem('token');
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/events/events-details', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      if (!response.ok) {
        throw new Error('Error al obtener los eventos');
      }
      const data = await response.json();
      if (data.length === 0) {
        setEmptyResponse(true);
      }
      setUpcomingEvents(data);
      setEvents(data);
    } catch (error) {
      setError(true);
      messageApi.error('Error al obtener los eventos. Por favor, refresca la página. ' + error.message);
      console.error('Error fetching upcoming events:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterEvents = (events, category) => {
    if (category === 'xv') {
      return events.filter(event => event.XVID > 1 && event.eventType !== 'rental' && event.name === null);
    }
    else if (category === 'wedding') {
      return events.filter(event => event.name);
    }
    else if (category === 'rental') {
      return events.filter(event => event.eventType === 'rental');
    }
  };

  const getTimeValue = (dateValue) => {
    if (!dateValue) return null;
    if (typeof dateValue.valueOf === 'function') {
      const val = dateValue.valueOf();
      return Number.isNaN(val) ? null : val;
    }
    const parsed = new Date(dateValue).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  };

  const applyFilters = () => {
    let filteredEvents = [...upcomingEvents];

    if (category) {
      filteredEvents = filterEvents(filteredEvents, category) || [];
    }

    if (dates && dates.length === 2) {
      const startTime = getTimeValue(dates[0]);
      const endTime = getTimeValue(dates[1]);

      if (startTime && endTime) {
        filteredEvents = filteredEvents.filter(event => {
          const eventStart = new Date(event.eventDateStart).getTime();
          const eventEnd = new Date(event.eventDateEnd).getTime();
          return eventStart >= startTime && eventEnd <= endTime;
        });
      }
    }

    if (search.trim()) {
      const searchLower = search.trim().toLowerCase();
      filteredEvents = filteredEvents.filter(event =>
        (event.clientName || '').toLowerCase().includes(searchLower)
      );
    }

    setEvents(filteredEvents);
  };

  const datesHandler = (values) => {
    if (values) {
      setDates(values);
    } else {
      setDates(null);
    }
  };

  // Componente para vista móvil de eventos
  const MobileEventView = ({ event, index }) => (
    <MobileEventCard key={event.eventID + "-" + (event.XVID ?? event.wpartnerID) + "-" + index}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: '#f759ab', mr: 2 }}>
            <EventAvailableIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
            {event.eventTitle}
          </Typography>
          <IconButton
            onClick={() => navigate(`/events/view-event-details/${event?.eventID}`)}
            size="small"
            sx={{ bgcolor: '#1976d2', color: 'white', '&:hover': { bgcolor: '#1565c0' } }}
          >
            <ListAltIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Chip
            icon={<EventIcon />}
            label={`${formatDate(event.eventDateStart)}`}
            size="small"
            sx={{ mb: 1, mr: 1 }}
            color="primary"
          />
          <Chip
            icon={<PersonIcon />}
            label={event.clientName}
            size="small"
            sx={{ mb: 1, mr: 1 }}
          />
          <Chip
            label={event.name ? `Pareja: ${event.name}` : `Quinceañera: ${event.XVName}`}
            size="small"
            sx={{ mb: 1, mr: 1 }}
            color="secondary"
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Chip
              label={`Pago: ${event.paymentStatus?.toUpperCase()}`}
              size="small"
              color={event.paymentStatus === 'pendiente' ? 'warning' : 'success'}
            />
          </Box>
          <IconButton
            onClick={() => setExpandedEvent(expandedEvent === index ? null : index)}
            size="small"
          >
            {expandedEvent === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expandedEvent === index}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <LocationOnIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
              <strong>Lugar:</strong> {event.eventLocation || 'Estudio fotográfico'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <DescriptionIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
              <strong>Descripción:</strong> {event.eventDescription}
            </Typography>
            <Typography variant="body2">
              <EventIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
              <strong>Fin:</strong> {formatDate(event.eventDateEnd)}
            </Typography>
          </Box>
        </Collapse>
      </CardContent>
    </MobileEventCard>
  );

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [upcomingEvents, category, dates, search]);

  useEffect(() => {
    if (initializedFromURL) return;
    const params = new URLSearchParams(location.search);
    let didInit = false;

    const q = params.get('search') || params.get('q');
    if (q) { setSearch(q); didInit = true; }

    const start = params.get('start') || params.get('from');
    const end = params.get('end') || params.get('to');
    if (start && end) {
      const mStart = moment(start, [moment.ISO_8601, 'YYYY-MM-DD'], true);
      const mEnd = moment(end, [moment.ISO_8601, 'YYYY-MM-DD'], true);
      if (mStart.isValid() && mEnd.isValid()) {
        const ordered = mStart.isAfter(mEnd) ? [mEnd, mStart] : [mStart, mEnd];
        setDates(ordered);
        didInit = true;
      }
    }

    const cat = params.get('category');
    if (cat) {
      setCategory(cat);
      didInit = true;
    }

    const status = params.get('status');
    if (status && ['pendiente', 'pagado'].includes(status.toLowerCase())) {
      setPaymentStatus(status.toLowerCase());
      didInit = true;
    }

    setInitializedFromURL(true);
    if (didInit) {
      // applyFilters se disparará por efecto existente
    }
  }, [location.search, initializedFromURL]);

  useEffect(() => {
    if (!initializedFromURL) return;
    const params = new URLSearchParams(location.search);

    if (search) params.set('search', search); else params.delete('search');

    if (dates && dates.length === 2) {
      params.set('start', dates[0].format('YYYY-MM-DD'));
      params.set('end', dates[1].format('YYYY-MM-DD'));
    } else {
      params.delete('start');
      params.delete('end');
    }

    if (category) params.set('category', category); else params.delete('category');

    if (paymentStatus) params.set('status', paymentStatus); else params.delete('status');

    const newSearch = params.toString();
    const target = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    if (target !== `${location.pathname}${location.search}`) {
      navigate(target, { replace: true });
    }
  }, [search, dates, category, paymentStatus, initializedFromURL]);

  return (
    <StyledPage>
      {contextHolder}
      {emptyResponse ? (
        <Container maxWidth="sm" sx={{ py: 8 }}>
          {contextHolder}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 2
          }}>
            <Avatar sx={{ bgcolor: '#6a1b9a ', width: 64, height: 64 }}>
              <InboxIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h5" sx={{ color: '#6a1b9a ', fontWeight: 700 }}>
              No hay eventos próximos
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
              No se encontraron eventos programados en esta categoría.
            </Typography>
          </Box>
        </Container>
      ) : (
        <Box>
          <StyledTitle
            variant={isMobile ? "h5" : "h4"}
            component="h1"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 1 : 2
            }}
          >
            <HistoryIcon
              sx={{
                color: '#1976d2',
                fontSize: isMobile ? '1.5rem' : '2rem'
              }}
            />
            Historial de Eventos
          </StyledTitle>

          <Grid container spacing={isMobile ? 2 : 3}>
            <Grid item xs={12}>
              <StyledCard>
                <FiltersContainer>
                  <Grid
                    container
                    spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
                    alignItems="flex-end"
                  >
                    <Grid item xs={12}>
                      <Typography
                        variant={isMobile ? "body1" : "subtitle1"}
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          mb: isMobile ? 1 : 0.5
                        }}
                      >
                        <FilterListIcon fontSize="small" />
                        Filtros
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
                      <FormControl fullWidth sx={{ alignSelf: 'flex-end' }}>
                        <InputLabel sx={{ fontWeight: 500 }}>
                          Categoría
                        </InputLabel>
                        <Select
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          variant="outlined"
                          sx={{
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              minHeight: 24
                            }
                          }}
                        >
                          <MenuItem value={null} sx={{ color: 'text.secondary' }}>
                            <AllInboxIcon fontSize="small" /> Todos
                          </MenuItem>
                          <MenuItem value="xv" sx={{ color: 'primary.main' }}>
                            <CakeIcon fontSize="small" /> Quinceaños
                          </MenuItem>
                          <MenuItem value="wedding" sx={{ color: 'secondary.main' }}>
                            <LocalActivityIcon fontSize="small" /> Bodas
                          </MenuItem>
                          <MenuItem value="rental" sx={{ color: 'success.main' }}>
                            <BusinessCenterIcon fontSize="small" /> Rentas
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
                      <FormControl fullWidth sx={{ alignSelf: 'flex-end' }}>
                        <InputLabel htmlFor="client-search" sx={{ fontWeight: 500 }}>
                          Buscar cliente
                        </InputLabel>
                        <Input
                          id="client-search"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={12} md={4} sx={{ display: 'flex' }}>
                      <FormControl
                        fullWidth
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          flexDirection: 'column',
                          justifyContent: 'flex-end',
                          height: '100%',
                          alignSelf: 'flex-end'
                        }}
                      >
                        <Typography sx={{ fontWeight: 500, mb: 1, fontSize: isMobile ? '0.875rem' : '1rem' }}>
                          Rango de fechas:
                        </Typography>
                        <RangePicker
                          onChange={(values) => datesHandler(values)}
                          placeholder={['Fecha inicial', 'Fecha final']}
                          size={isMobile ? "middle" : "large"}
                          sx={{
                            width: '100%',
                            minWidth: 0
                          }}
                          slotProps={{
                            textField: {
                              variant: 'outlined',
                            },
                          }}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </FiltersContainer>
                <StyledCardContent>
                  {isLoading ? (
                    <Box sx={{ width: '100%' }}>
                      {[...Array(3)].map((_, i) => (
                        <Skeleton
                          key={i}
                          animation="wave"
                          height={isMobile ? 120 : 80}
                          sx={{ mb: 2, borderRadius: '8px' }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <>
                      {/* Vista móvil */}
                      {isMobile ? (
                        <Box>
                          {events.length > 0 ? (
                            events.map((event, index) => (
                              <MobileEventView key={event.eventID + "-" + (event.XVID ?? event.wpartnerID) + "-" + index} event={event} index={index} />
                            ))
                          ) : (
                            <MobileEventCard>
                              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                <Avatar sx={{ bgcolor: '#f759ab', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                                  <EventAvailableIcon />
                                </Avatar>
                                <Typography variant="h6" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                  No hay eventos registrados.
                                </Typography>
                              </CardContent>
                            </MobileEventCard>
                          )}
                        </Box>
                      ) : (
                        /* Vista desktop/tablet */
                        <StyledList sx={{ width: '100%', overflowY: 'auto', maxHeight: 600 }}>
                          {events.length > 0 ? (
                            events.map((event, index) => (
                              <React.Fragment key={event.eventID + "-" + (event.XVID ?? event.wpartnerID) + "-" + index}>
                                <ListItem alignItems="flex-start">
                                  <Avatar sx={{ bgcolor: '#f759ab', marginRight: 2, display: { xs: 'none', sm: 'flex' } }}>
                                    <EventAvailableIcon />
                                  </Avatar>
                                  <StyledListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Avatar sx={{ bgcolor: '#f759ab', display: { xs: 'flex', sm: 'none' }, width: 32, height: 32 }}>
                                          <EventAvailableIcon fontSize="small" />
                                        </Avatar>
                                        <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                                          {event.eventTitle}
                                        </Typography>
                                      </Box>
                                    }
                                    secondary={
                                      <Grid container spacing={1} sx={{ mt: 1 }}>
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="body2" color="primary" component="div" sx={{ mb: 1 }}>
                                            <EventIcon sx={{ color: 'green', marginRight: 1 }} fontSize="small" />
                                            {`Inicio: ${formatDate(event.eventDateStart)}`}
                                          </Typography>
                                          <Typography variant="body2" color="textPrimary" component="div" sx={{ mb: 1 }}>
                                            <EventIcon sx={{ color: 'red', marginRight: 1 }} fontSize="small" />
                                            {`Final: ${formatDate(event.eventDateEnd)}`}
                                          </Typography>
                                          <Typography variant="body2" color="textPrimary" component="div" sx={{ mb: 1 }}>
                                            <PersonIcon sx={{ color: 'blue', marginRight: 1 }} fontSize="small" />
                                            {`Cliente: ${event.clientName}`}
                                          </Typography>
                                          <Typography variant="body2" color="textPrimary" component="div">
                                            <PersonIcon sx={{ color: 'purple', marginRight: 1 }} fontSize="small" />
                                            {event.name ? `Pareja: ${event.name}` : `Quinceañera: ${event.XVName}`}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="body2" color="textPrimary" component="div" sx={{ mb: 1 }}>
                                            <LocationOnIcon sx={{ color: 'yellowgreen', marginRight: 1 }} fontSize="small" />
                                            {`Lugar: ${event.eventLocation || 'Estudio fotográfico'}`}
                                          </Typography>
                                          <Typography variant="body2" color="textPrimary" component="div" sx={{ mb: 1 }}>
                                            <DescriptionIcon sx={{ color: 'purple', marginRight: 1 }} fontSize="small" />
                                            {`Descripción: ${event.eventDescription}`}
                                          </Typography>
                                          <Typography variant="body2" color="textPrimary" component="div">
                                            <PaymentIcon sx={{ color: 'brown', marginRight: 1 }} fontSize="small" /> Pago:
                                            <span style={{ color: event.paymentStatus === 'pendiente' ? 'orange' : 'greenyellow' }}>{` ${event.paymentStatus?.toUpperCase()}`}</span>
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    }
                                  />
                                  <Tooltip title="Ver detalles" arrow>
                                    <IconButton
                                      onClick={() => navigate(`/events/view-event-details/${event.eventID}`)}
                                      aria-label="Ver detalles del evento"
                                      sx={{ alignSelf: 'flex-start' }}
                                    >
                                      <Avatar sx={{ bgcolor: '#1976d2' }}>
                                        <ListAltIcon />
                                      </Avatar>
                                    </IconButton>
                                  </Tooltip>
                                </ListItem>
                                <Divider variant="inset" component="li" />
                              </React.Fragment>
                            ))
                          ) : (
                            <ListItem>
                              <Avatar sx={{ bgcolor: '#f759ab', marginRight: 2 }}>
                                <EventAvailableIcon />
                              </Avatar>
                              <ListItemText
                                primary="No hay eventos registrados."
                                primaryTypographyProps={{ color: 'textSecondary', fontStyle: 'italic' }}
                              />
                            </ListItem>
                          )}
                        </StyledList>
                      )}
                    </>
                  )}
                </StyledCardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </Box>
        )}
    </StyledPage>
  );
}

export default ViewEvents;